import React, {useState} from 'react';
import EasasPageTemplateWrapper from "../../templates/EasasPageTemplateWrapper";
import {Button} from "primereact/button";
import {FilesApi, FileUploadDTO} from "api/FilesApi";
import {
    useDeleteCompanyFile,
    useDeletePublicFile,
    useGetCompanyFiles,
    useGetPublicFiles
} from "api/api-hooks/file-hooks";
import {TabPanel, TabView} from 'primereact/tabview';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import FileDialog from "./FileUploadDialog";
import {Tag} from "primereact/tag";
import {PermissionGuard} from "../../wrappers/PermissionGuard";
import {useHasPermissions} from "hooks/useHasPermissions";
import {JodaTimeUtils} from "../../../utils/joda-time-utils";


export default function CompanyFilesPage() {
    const isAllowedCompanyView = useHasPermissions("COMPANY_FILES_VIEW");

    const publicFiles = useGetPublicFiles();
    const companyFiles = useGetCompanyFiles(isAllowedCompanyView);

    const [isUploadPublic, setIsUploadPublic] = useState(false);
    const [fileDialogVisible, setFileDialogVisible] = useState(false);

    const tableRowButtonTemplate = (rowData: FileUploadDTO) => {
        return <>
            <DownloadButton file={rowData}/>

            <PermissionGuard permissions={rowData.isPublic ? "PUBLIC_FILES_EDIT" : "COMPANY_FILES_EDIT"}>
                <DeleteButton file={rowData}/>
            </PermissionGuard>
        </>
    }

    const tagsBodyTemplate = (rowData: any) => {
        return rowData.tags?.map((tag: string) => <Tag value={tag} className="p-mr-2"/>)
    }

    const createdAtBodyTemplate = (rowData: FileUploadDTO) => {
         return <span>{JodaTimeUtils.formatInstantAsLocal(rowData.createdAt)}</span>
    }

    const editedAtBodyTemplate = (rowData: FileUploadDTO) => {
        return <span>{JodaTimeUtils.formatInstantAsLocal(rowData.editedAt)}</span>
    }



    return <EasasPageTemplateWrapper breadcrumb={["Company files"]} title="Files" cardStyle={{height: "100%"}}>
        <TabView activeIndex={0}>
            <TabPanel header="Public files">
                <PermissionGuard permissions={"PUBLIC_FILES_EDIT"}>
                    <Button label="Upload file..."
                            icon="pi pi-upload"
                            className="p-mb-4"
                            onClick={() => {
                                setFileDialogVisible(true)
                                setIsUploadPublic(true)
                            }}
                    />
                </PermissionGuard>

                <div>
                    {publicFiles.isLoading && "Loading...."}
                    {publicFiles.isError && "Error loading files"}
                </div>

                <DataTable value={publicFiles.data ?? []}>
                    <Column field="fileName" header="File Name"></Column>
                    <Column field="description" header="Description"/>
                    <Column header="Tags" body={tagsBodyTemplate}/>
                    <Column header="Edited" body={editedAtBodyTemplate}/>

                    <Column body={tableRowButtonTemplate} style={{width: "100px"}}/>
                </DataTable>
            </TabPanel>
            { isAllowedCompanyView &&
                <TabPanel header="Company files">
                    <PermissionGuard permissions={"COMPANY_FILES_EDIT"}>
                        <Button label="Upload file..."
                                icon="pi pi-upload"
                                className="p-mb-4"
                                onClick={() => {
                                    setFileDialogVisible(true)
                                    setIsUploadPublic(false)
                                }}
                        />
                    </PermissionGuard>

                    <div>
                        {companyFiles.isLoading && "Loading...."}
                        {companyFiles.isError && "Error loading files"}
                    </div>

                    <DataTable value={companyFiles.data ?? []}>
                        <Column field="fileName" header="File Name"></Column>
                        <Column field="description" header="Description"/>
                        <Column field="tags" header="Tags" body={tagsBodyTemplate}/>
                        <Column header="Created" body={createdAtBodyTemplate}/>

                        <Column body={tableRowButtonTemplate} style={{width: "100px"}}/>
                    </DataTable>
                </TabPanel>
            }
    </TabView>

        <FileDialog visible={fileDialogVisible} setVisible={setFileDialogVisible} isPublic={isUploadPublic}/>
    </EasasPageTemplateWrapper>
}

function DownloadButton(props: {
    file: FileUploadDTO
}) {
    const downloadFile = () => {
        const url = props.file.isPublic ?
            FilesApi.downloadPublicFileUrl(props.file.downloadUrlPart)
            : FilesApi.downloadCompanyFileUrl(props.file.downloadUrlPart);

        window.open(url, '_blank');
    }

    return <Button icon="pi pi-download" className="p-button-rounded p-button-text" onClick={downloadFile}></Button>
}

function DeleteButton(props: {
    file: FileUploadDTO
}) {
    const deleteMutation = props.file.isPublic ? useDeletePublicFile() : useDeleteCompanyFile();

    const deleteFile = () => {
        deleteMutation.mutate(props.file.downloadUrlPart);
    }

    return <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger"
                   onClick={deleteFile}></Button>
}